import { createAsyncThunk } from '@reduxjs/toolkit';
import fileDownload from 'js-file-download';

import { getPersonsDocumentsTypes } from 'pages/dashboard/selectors';
import { authorizeSignatory } from 'pages/Profile/thunks';
import { getProfile } from 'pages/Profile/selectors';
import { buildFormData } from 'utils/helpers';
import { RootState } from 'state/store';
import {
  AuthorizedSignatorySchema,
  getAuthorizedSignatories,
  saveAuthorizedSignatories,
  getAuthorizedSignatoriesFile,
} from 'services/authorizedSignatories';

import { getMappedSaveSignatoryFields } from './utils';

const GOVERNMENT_ID_TYPE_PATTERN = /government/i;

const getGovernmentIdTypeId = (state: RootState) => {
  const types = getPersonsDocumentsTypes(state);

  const governmentIdType = types.find((type: any) =>
    GOVERNMENT_ID_TYPE_PATTERN.test(type.description || ''),
  );

  return (
    governmentIdType?.personDocumentsTypesId ||
    types[0]?.personDocumentsTypesId ||
    null
  );
};

export const fetchSignatoryDetails: any = createAsyncThunk(
  'submitDashboard/fetchSignatoryDetails',
  async (_: void, thunkApi) => {
    try {
      const { person, business } = getProfile(thunkApi.getState() as RootState);

      if (!business?.businessesId || !person?.personsId) {
        return null;
      }

      const signatory = await getAuthorizedSignatories(
        business.businessesId,
        person.personsId,
      );

      return signatory || null;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const saveSignatoryDetails: any = createAsyncThunk(
  'submitDashboard/saveSignatoryDetails',
  async ({ data, fileData }: any, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;

      const { person, business } = getProfile(state);

      const fields = getMappedSaveSignatoryFields({
        data,
        fileData,
        person,
        business,
        personDocumentsTypesId: getGovernmentIdTypeId(state),
      });

      await saveAuthorizedSignatories(buildFormData(fields));

      const result: any = await thunkApi.dispatch(authorizeSignatory());

      if (result.error) {
        // the details were stored - refresh them so a retry doesn't
        // resubmit stale values from before this save
        let signatory: AuthorizedSignatorySchema | null = null;

        try {
          signatory = await getAuthorizedSignatories(
            business.businessesId,
            person.personsId,
          );
        } catch (e) {
          signatory = null;
        }

        return thunkApi.rejectWithValue({ stage: 'attestation', signatory });
      }

      try {
        const signatory = await getAuthorizedSignatories(
          business.businessesId,
          person.personsId,
        );

        return signatory || null;
      } catch (e) {
        // the save and attestation succeeded - a failed refresh must not
        // surface as a save error (the slice keeps existing data on null)
        return null;
      }
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const downloadSignatoryDocument: any = createAsyncThunk(
  'submitDashboard/downloadSignatoryDocument',
  async ({ guid, fileName }: any, thunkApi) => {
    try {
      const { business } = getProfile(thunkApi.getState() as RootState);

      const file = await getAuthorizedSignatoriesFile(
        business.businessesId,
        guid,
      );

      fileDownload(file, fileName);
    } catch (e) {
      return Promise.reject(e);
    }
  },
);
